import { useMutation } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Page,
  PageHeader,
  Select,
  StatCard,
  StatCardList,
  Table,
  linkTextClasses,
  type Column,
} from '@evogard/ui';
import {
  BILLING_AWARD_KIND_LABELS,
  csvCell,
  csvContent,
  csvDecimal,
  format as formatMoney,
  formatDate,
  formatInstant,
  sum,
  toColumn,
  type BillingAwardDay,
  type BillingAwardOperatorResult,
  type Money,
} from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { saveCsv } from '../../../lib/csv.js';
import { ReasonDialog } from './AwardDialogs.js';
import { weekdayOf } from './AwardsOperatorScreen.js';
import {
  currentPeriod,
  mergeAwards,
  periodLabel,
  recentPeriods,
  useAwardPeriod,
  useInvalidateAwards,
  type AwardTenantState,
  type AwardOperatorLine,
  type AwardsMerge,
} from './useAwards.js';

type Calculated = Extract<AwardsMerge, { state: 'calculado' }>;
type Row = AwardOperatorLine & { result: BillingAwardOperatorResult };

/**
 * A premiação da cobrança — o painel da `PremiacaoCobrancaRetencao`, com o
 * relatório de pagamento (21/10/2026).
 *
 * Cada base responde a sua parte; a tela soma CE e GO e só então aplica a
 * faixa (G6), com `billingAwardsOf` — a mesma função do domínio que o
 * servidor usaria. O relatório de pagamento só sai com as DUAS bases fechadas:
 * a faixa é sobre a soma, e uma base só pagaria a menos sem avisar.
 */
export function AwardsScreen() {
  const search = useSearch({ from: '/authenticated/billing/awards' });
  const [period, setPeriod] = useState(search.period ?? currentPeriod());
  const states = useAwardPeriod(period);
  const merged = mergeAwards(states);
  const loading = states.some((b) => b.state === 'carregando');
  const ready = states.flatMap((b) => (b.state === 'pronta' ? [b] : []));

  return (
    <Page measure="cheia">
      <PageHeader title="Premiação da cobrança" />

      <div data-print="fora" className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select label="Competência" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {recentPeriods().map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)}
              </option>
            ))}
          </Select>
        </div>
        <nav className="flex flex-wrap gap-4 text-sm">
          <Link to="/billing/awards/links" className={linkTextClasses()}>
            Monitoramento
          </Link>
          <Link to="/billing/awards/data" className={linkTextClasses()}>
            Dados
          </Link>
          <Link to="/billing/awards/log" className={linkTextClasses()}>
            Log
          </Link>
          <Link to="/billing/awards/settings" className={linkTextClasses()}>
            Configuração
          </Link>
        </nav>
      </div>

      {states.map((b) =>
        b.state === 'erro' ? (
          <Alert key={b.tenant.slug} tone="erro">
            {b.message}
          </Alert>
        ) : null,
      )}
      {states.some((b) => b.state === 'sem_permissao') && ready.length > 0 && (
        <Alert tone="alerta">
          Você não vê a premiação de{' '}
          {states
            .filter((b) => b.state === 'sem_permissao')
            .map((b) => b.tenant.name)
            .join(' e ')}
          . O resultado abaixo é só das outras bases, e a faixa sobre ele não é a do pagamento — o
          relatório não sai assim.
        </Alert>
      )}

      {!loading && ready.length === 0 && !states.some((b) => b.state === 'erro') && (
        <EmptyState icon="grafico" title="Nenhuma premiação para mostrar">
          Seu grupo não tem a permissão de ver a premiação em nenhuma das suas bases.
        </EmptyState>
      )}

      <div data-print="fora" className="grid gap-3 lg:grid-cols-2">
        {ready.map((b) => (
          <TenantCard key={b.tenant.slug} entry={b} period={period} />
        ))}
      </div>

      {merged.state === 'recusado' && <Alert tone="erro">{merged.problem}</Alert>}
      {merged.state === 'calculado' && <Result merged={merged} period={period} states={states} />}
    </Page>
  );
}

/** Uma base: aberta ou fechada, o último recálculo, os atendimentos e os atos. */
function TenantCard({ entry, period }: { entry: Extract<AwardTenantState, { state: 'pronta' }>; period: string }) {
  const invalidate = useInvalidateAwards();
  const [reopening, setReopening] = useState(false);
  const { view, tenant } = entry;
  const params = { tenant: tenant.slug };
  const sync = useMutation({
    mutationFn: async () => ok(await api.billingAwards.sync({ params, body: { period } }), 200, 'Não foi possível recalcular.'),
    onSuccess: () => invalidate(),
  });
  const verify = useMutation({
    mutationFn: async () =>
      ok(await api.billingAwards.verify({ params, body: { period } }), 202, 'Não foi possível pedir a conferência.'),
  });
  const request = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingAwards.requestRecords({ params, body: { period } }),
        202,
        'Não foi possível pedir os atendimentos ao ERP.',
      ),
  });
  const close = useMutation({
    mutationFn: async () =>
      ok(await api.billingAwards.close({ params, body: { period } }), 200, 'Não foi possível fechar a competência.'),
    onSuccess: () => invalidate(),
  });
  const reopen = useMutation({
    mutationFn: async (reason: string) =>
      ok(await api.billingAwards.reopen({ params, body: { period, reason } }), 200, 'Não foi possível reabrir.'),
    onSuccess: () => {
      setReopening(false);
      invalidate();
    },
  });
  const open = view.closing === null;
  const legacy = view.closing?.source === 'premiacao_legado';
  const ended = period < currentPeriod();
  const error = sync.error ?? verify.error ?? close.error ?? request.error;

  return (
    <Card
      title={tenant.name}
      descricao={
        open
          ? view.lastMatch
            ? `Aberta. Vínculos recalculados em ${formatInstant(view.lastMatch.at)}${view.lastMatch.by ? ` por ${view.lastMatch.by}` : ', pela rotina'}.`
            : 'Aberta. Os vínculos desta competência ainda não foram calculados.'
          : legacy
            ? `Trazida fechada do sistema antigo em ${formatInstant(view.closing!.closedAt)}. O valor é o que a tela de lá mostrava, congelado: não se recalcula nem reabre. O sistema antigo não guarda a régua de cada mês, então o valor é o calculado com a régua que ele tinha no dia da importação.`
            : `Fechada em ${formatInstant(view.closing!.closedAt)}${view.closing!.closedBy ? ` por ${view.closing!.closedBy}` : ''}. Os vínculos e a régua estão congelados.`
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          {open ? <Badge tone="informacao">Aberta</Badge> : <Badge tone="sucesso">Fechada</Badge>}
          {/*
           * No mês do sistema antigo, a contagem de atendimentos daqui não diz nada
           * sobre o valor: ele veio congelado de lá, e os atendimentos daquele mês
           * só estão no núcleo se alguém os buscou no SGA.
           */}
          <span className="text-texto-suave">
            {legacy
              ? 'Valor congelado do sistema antigo — os atendimentos daqui não entram nele.'
              : `${view.records.total} atendimento(s) do SGA na competência` +
                (view.records.lastSyncedAt ? ` · o mais novo chegou do ERP em ${formatInstant(view.records.lastSyncedAt)}` : '')}
          </span>
        </div>
        {!legacy && view.records.total === 0 && (
          <Alert tone="alerta">
            Nenhum atendimento do SGA nesta competência. Sem atendimento não há vínculo, e a premiação é
            zero. A esteira do ERP traz os atendimentos do dia; para o mês inteiro, use "Buscar os
            atendimentos no SGA".
          </Alert>
        )}
        {open && !view.previousClosed && (
          <p className="text-texto-suave">
            A competência anterior não está fechada: a primeira semana do mês começa nela, e o pago dela
            ainda pode mudar.
          </p>
        )}
        {error && <Alert tone="erro">{error.message}</Alert>}
        {sync.isSuccess && (
          <Alert tone="sucesso">
            {sync.data.links} vínculo(s): {sync.data.byStatus.retido ?? 0} pago(s), {sync.data.byStatus.monitorando ?? 0}{' '}
            monitorando.
          </Alert>
        )}
        {request.isSuccess && (
          <Alert tone="sucesso">
            {request.data.queued
              ? 'O ERP vai buscar os atendimentos do mês no SGA. O mês corrente se recalcula sozinho quando eles chegarem; o mês que virou, recalcule depois.'
              : 'Esse pedido já estava na fila do ERP.'}
          </Alert>
        )}
        {verify.isSuccess && (
          <Alert tone="sucesso">A conferência no SGA foi pedida. O resultado aparece no log quando terminar.</Alert>
        )}
        <div className="flex flex-wrap gap-2">
          {open && view.can.sync && (
            <>
              <Button size="sm" loading={sync.isPending} onClick={() => sync.mutate()}>
                Recalcular os vínculos
              </Button>
              <Button size="sm" variant="contorno" loading={request.isPending} onClick={() => request.mutate()}>
                Buscar os atendimentos no SGA
              </Button>
              <Button size="sm" variant="contorno" loading={verify.isPending} onClick={() => verify.mutate()}>
                Conferir os boletos no SGA
              </Button>
            </>
          )}
          {open && ended && view.can.close && (
            <Button size="sm" variant="contorno" loading={close.isPending} onClick={() => close.mutate()}>
              Fechar a competência
            </Button>
          )}
          {!open && view.can.close && !legacy && (
            <Button size="sm" variant="discreto" onClick={() => setReopening(true)}>
              Reabrir
            </Button>
          )}
        </div>
      </div>
      {reopening && (
        <ReasonDialog
          title={`Reabrir ${periodLabel(period)} — ${tenant.name}`}
          descricao="Reaberta, a competência volta a recalcular com a régua vigente, e o relatório deixa de sair até ela fechar de novo."
          confirm="Reabrir"
          pending={reopen.isPending}
          error={reopen.error?.message ?? null}
          onConfirm={(reason) => reopen.mutate(reason)}
          onClose={() => setReopening(false)}
        />
      )}
    </Card>
  );
}

function Result({ merged, period, states }: { merged: Calculated; period: string; states: readonly AwardTenantState[] }) {
  const { result, rule, lines } = merged;
  const rows: Row[] = lines.map((l) => ({ ...l, result: result.operators.find((o) => o.operatorId === l.id)! }));
  const team = sum(...rows.map((r) => r.result.recovered));
  const bonus = sum(...rows.map((r) => r.result.total));
  const paid = rows.reduce((n, r) => n + r.result.paidCount, 0);
  const tenants = states.map((b) => b.tenant);
  const reportReady = merged.closed && merged.missing.length === 0;
  const totals = totalsOf(rows, tenants);

  return (
    <>
      <StatCardList>
        <StatCard icon="dinheiro" label="Recuperado pela equipe" value={formatMoney(team)} support={`${paid} boleto(s) pago(s)`} />
        {/*
         * O recuperado de cada base, como o "Distribuição CE × GO" do painel
         * do legado (`dashboard/page.tsx`), pedido pelo dono em 29/10/2026: a
         * mesma equipe cobra as duas praças, e a líder lê numa tela só quanto
         * veio de cada uma. É só o recuperado — a bonificação não se reparte por
         * base, porque a faixa vale sobre a soma.
         */}
        <StatCard
          icon="grafico"
          label={`Distribuição ${tenants.map((t) => t.slug.toUpperCase()).join(' × ')}`}
          value={
            <span className="flex flex-col">
              {tenants.map((t) => (
                <span key={t.slug} className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-texto-suave">{t.slug.toUpperCase()}</span>
                  {formatMoney(totals.byTenant[t.slug] ?? sum())}
                </span>
              ))}
            </span>
          }
          support="o recuperado de cada base, somado ao lado"
        />
        <StatCard icon="grafico" label="Bonificação dos operadores" value={formatMoney(bonus)} support="diária, semanal e mensal" />
        <StatCard
          icon="percentual"
          label="Liderança"
          value={result.leadership ? formatMoney(result.leadership.bonus) : '—'}
          support={
            result.leadership
              ? result.leadership.tier
                ? `${result.leadership.tier.label}, sobre o total da equipe`
                : 'a equipe ainda não alcançou a primeira faixa'
              : 'desligada na régua'
          }
        />
      </StatCardList>

      <Card
        title={`Resultado de ${periodLabel(period)}`}
        descricao="Recuperado é o valor pago dos boletos com vínculo que conta: pago no mesmo dia de um atendimento do operador, no mesmo veículo, num tipo de boleto dele. As faixas valem sobre a soma das bases."
        bleed
      >
        <Table<Row>
          label="Resultado de cada operador na competência"
          empty="Nenhum operador ativo na premiação destas bases."
          items={rows}
          rowKey={(r) => r.id}
          paginate={false}
          columns={columns(tenants, rule.enabled, period, merged.frozen, totals)}
        />
      </Card>

      {rule.enabled.semanal && (
        <Card
          title="Semanas"
          descricao="De segunda a domingo; a semana é do mês que tem o domingo, e soma o pago do fim do mês anterior."
          bleed
        >
          <Table<Row>
            label="Recuperado de cada operador por semana"
            empty="Nenhum operador."
            items={rows}
            rowKey={(r) => r.id}
            paginate={false}
            columns={[
              { title: 'Operador', alignment: 'inicio', cell: (r) => r.name },
              ...(rows[0]?.result.weeks ?? []).map(
                (w, i): Column<Row> => ({
                  title: `${formatDate(w.start).slice(0, 5)} a ${formatDate(w.end).slice(0, 5)}`,
                  alignment: 'fim',
                  cell: (r) => {
                    const week = r.result.weeks[i]!;
                    return (
                      <span className="flex flex-col">
                        <span>{formatMoney(week.recovered)}</span>
                        {week.tier && <span className="text-xs text-sucesso">{week.tier.label} · {formatMoney(week.bonus)}</span>}
                      </span>
                    );
                  },
                }),
              ),
            ]}
          />
        </Card>
      )}

      {rule.enabled.diaria && (
        <Card
          title="Diárias batidas"
          descricao="Cada dia em que um operador alcançou uma faixa da meta diária, pelo dia do pagamento — a aba 'Diárias batidas' do relatório de hoje."
          bleed
        >
          <Table<DailyHit>
            label="Dias em que cada operador bateu a meta diária"
            empty="Nenhum operador alcançou a primeira faixa diária nesta competência."
            items={dailyHits(rows)}
            rowKey={(h) => `${h.operatorId}:${h.day.date}`}
            paginate={false}
            columns={[
              { title: 'Operador', alignment: 'inicio', cell: (h) => h.name },
              { title: 'Data', alignment: 'inicio', cell: (h) => formatDate(h.day.date) },
              { title: 'Dia da semana', alignment: 'inicio', cell: (h) => weekdayOf(h.day.date) },
              { title: 'Recuperado', alignment: 'fim', cell: (h) => formatMoney(h.day.recovered) },
              { title: 'Faixa', alignment: 'inicio', cell: (h) => h.day.tier?.label ?? '—' },
              { title: 'Bonificação', alignment: 'fim', cell: (h) => <strong>{formatMoney(h.day.bonus)}</strong> },
            ]}
          />
        </Card>
      )}

      <Card
        title="Relatório de pagamento"
        descricao="Uma linha por operador, com a bonificação de cada meta e a faixa aplicada."
      >
        <div data-print="fora" className="flex flex-col gap-3">
          {!reportReady && (
            <Alert tone="informacao">
              O relatório sai quando {tenants.map((t) => t.name).join(' e ')} estiverem fechadas
              {merged.missing.length > 0 ? ` e respondendo (falta ${merged.missing.map((t) => t.name).join(', ')})` : ''}.
              Antes disso, o resultado acima é prévia.
            </Alert>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="contorno"
              disabled={!reportReady}
              onClick={() => downloadReport(period, rows, tenants, result.leadership, merged.frozen)}
            >
              Exportar CSV
            </Button>
            {rule.enabled.diaria && (
              <Button
                variant="contorno"
                disabled={!reportReady}
                onClick={() => downloadDailyHits(period, rows, tenants)}
              >
                Exportar diárias batidas
              </Button>
            )}
            <Button variant="contorno" disabled={!reportReady} onClick={() => window.print()}>
              Imprimir
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}

/** A equipe somada — a linha de TOTAL da tabela, como a do painel do legado. */
interface Totals {
  records: number;
  paidCount: number;
  byTenant: Record<string, Money>;
  recovered: Money;
  dailyBonus: Money;
  weeklyBonus: Money;
  monthlyBonus: Money;
  total: Money;
}

/**
 * A soma de todos os operadores, sobre a lista INTEIRA (a tabela não pagina).
 * É o rodapé do "Resultado" e o cartão da distribuição por base (29/10/2026).
 */
function totalsOf(rows: readonly Row[], tenants: ReadonlyArray<{ slug: string }>): Totals {
  return {
    records: rows.reduce((n, r) => n + r.records, 0),
    paidCount: rows.reduce((n, r) => n + r.result.paidCount, 0),
    byTenant: Object.fromEntries(tenants.map((t) => [t.slug, sum(...rows.map((r) => r.byTenant[t.slug] ?? sum()))])),
    recovered: sum(...rows.map((r) => r.result.recovered)),
    dailyBonus: sum(...rows.map((r) => r.result.dailyBonus)),
    weeklyBonus: sum(...rows.map((r) => r.result.weeklyBonus)),
    monthlyBonus: sum(...rows.map((r) => r.result.monthlyBonus)),
    total: sum(...rows.map((r) => r.result.total)),
  };
}

function columns(
  tenants: ReadonlyArray<{ slug: string; name: string }>,
  enabled: Record<'diaria' | 'semanal' | 'mensal' | 'lideranca', boolean>,
  period: string,
  /** o mês veio congelado do sistema antigo: os atendimentos daqui não são os dele */
  frozen: boolean,
  /** a linha de TOTAL no pé da tabela (29/10/2026) */
  totals: Totals,
): Array<Column<Row>> {
  return [
    {
      title: 'Operador',
      alignment: 'inicio',
      footer: 'Total',
      cell: (r) => (
        <span className="flex flex-col">
          <Link
            to="/billing/awards/operator/$operatorId"
            params={{ operatorId: r.id }}
            search={{ period }}
            className={linkTextClasses('font-medium')}
          >
            {r.name}
          </Link>
          {(r.monitoring > 0 || r.invalidated > 0) && (
            <span className="text-xs text-texto-suave">
              {[r.monitoring > 0 ? `${r.monitoring} monitorando` : null, r.invalidated > 0 ? `${r.invalidated} invalidado(s)` : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </span>
      ),
    },
    { title: 'Atendimentos', alignment: 'fim', cell: (r) => (frozen ? '—' : r.records), footer: frozen ? '—' : totals.records },
    { title: 'Pagos', alignment: 'fim', cell: (r) => r.result.paidCount, footer: totals.paidCount },
    ...tenants.map(
      (t): Column<Row> => ({
        title: t.slug.toUpperCase(),
        alignment: 'fim',
        cell: (r) => formatMoney(r.byTenant[t.slug] ?? sum()),
        footer: formatMoney(totals.byTenant[t.slug] ?? sum()),
      }),
    ),
    {
      title: 'Recuperado',
      alignment: 'fim',
      cell: (r) => <strong>{formatMoney(r.result.recovered)}</strong>,
      footer: formatMoney(totals.recovered),
    },
    ...(enabled.diaria
      ? [
          {
            title: BILLING_AWARD_KIND_LABELS.diaria,
            alignment: 'fim' as const,
            cell: (r: Row) => formatMoney(r.result.dailyBonus),
            footer: formatMoney(totals.dailyBonus),
          },
        ]
      : []),
    ...(enabled.semanal
      ? [
          {
            title: BILLING_AWARD_KIND_LABELS.semanal,
            alignment: 'fim' as const,
            cell: (r: Row) => formatMoney(r.result.weeklyBonus),
            footer: formatMoney(totals.weeklyBonus),
          },
        ]
      : []),
    ...(enabled.mensal
      ? [
          {
            title: BILLING_AWARD_KIND_LABELS.mensal,
            alignment: 'fim' as const,
            cell: (r: Row) => (
              <span className="flex flex-col">
                <span>{formatMoney(r.result.monthlyBonus)}</span>
                {r.result.monthlyTier && <span className="text-xs text-texto-suave">{r.result.monthlyTier.label}</span>}
              </span>
            ),
            footer: formatMoney(totals.monthlyBonus),
          },
        ]
      : []),
    {
      title: 'Bonificação',
      alignment: 'fim',
      cell: (r) => <strong>{formatMoney(r.result.total)}</strong>,
      footer: formatMoney(totals.total),
    },
  ];
}

/** Um dia em que um operador alcançou uma faixa da meta diária. */
interface DailyHit {
  operatorId: string;
  name: string;
  day: BillingAwardDay;
}

/** As diárias batidas de todos os operadores, por nome e dia. */
function dailyHits(rows: readonly Row[]): DailyHit[] {
  return rows
    .flatMap((r) => r.result.days.filter((d) => d.tier !== null).map((day) => ({ operatorId: r.id, name: r.name, day })))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR') || a.day.date.localeCompare(b.day.date));
}

/**
 * As diárias batidas em CSV — a aba 2 do Excel da `PremiacaoCobrancaRetencao`
 * (`relatorio-excel.ts`, "Diárias batidas"), com as mesmas regras do
 * relatório de pagamento: só com as bases fechadas.
 */
function downloadDailyHits(
  period: string,
  rows: readonly Row[],
  tenants: ReadonlyArray<{ slug: string; name: string }>,
): void {
  const money = (m: Parameters<typeof toColumn>[0]) => csvDecimal(toColumn(m));
  const content = csvContent([
    ['Operador', 'Data', 'Dia da semana', 'Recuperado', 'Faixa', 'Bonificação'].map(csvCell),
    ...dailyHits(rows).map((h) => [
      csvCell(h.name),
      csvCell(formatDate(h.day.date)),
      csvCell(weekdayOf(h.day.date)),
      money(h.day.recovered),
      csvCell(h.day.tier?.label ?? ''),
      money(h.day.bonus),
    ]),
    [],
    [csvCell('Competência'), csvCell(periodLabel(period))],
    [csvCell('Bases'), csvCell(tenants.map((t) => t.name).join(' e '))],
    [csvCell('Gerado em'), csvCell(formatInstant(new Date().toISOString()))],
  ]);
  saveCsv(content, `premiacao-diarias-${period}`, tenants.map((t) => t.slug).join('-'));
}

/** O relatório de pagamento em CSV — o que vai para quem paga. */
function downloadReport(
  period: string,
  rows: readonly Row[],
  tenants: ReadonlyArray<{ slug: string; name: string }>,
  leadership: Calculated['result']['leadership'],
  /** o mês veio congelado do sistema antigo: os atendimentos daqui não são os dele */
  frozen: boolean,
): void {
  const header = [
    'Operador',
    'Atendimentos',
    'Pagos',
    ...tenants.map((t) => `Recuperado ${t.slug.toUpperCase()}`),
    'Recuperado',
    'Meta diária',
    'Meta semanal',
    'Meta mensal',
    'Faixa mensal',
    'Bonificação',
  ];
  const money = (m: Parameters<typeof toColumn>[0]) => csvDecimal(toColumn(m));
  const lines = rows.map((r) => [
    csvCell(r.name),
    csvCell(frozen ? '' : String(r.records)),
    csvCell(String(r.result.paidCount)),
    ...tenants.map((t) => money(r.byTenant[t.slug] ?? sum())),
    money(r.result.recovered),
    money(r.result.dailyBonus),
    money(r.result.weeklyBonus),
    money(r.result.monthlyBonus),
    csvCell(r.result.monthlyTier?.label ?? ''),
    money(r.result.total),
  ]);
  const content = csvContent([
    header.map(csvCell),
    ...lines,
    [],
    ...(leadership
      ? [[csvCell('Liderança'), csvCell(leadership.tier?.label ?? 'sem faixa'), money(leadership.bonus)]]
      : []),
    [csvCell('Competência'), csvCell(periodLabel(period))],
    [csvCell('Bases'), csvCell(tenants.map((t) => t.name).join(' e '))],
    [csvCell('Gerado em'), csvCell(formatInstant(new Date().toISOString()))],
  ]);
  saveCsv(content, `premiacao-cobranca-${period}`, tenants.map((t) => t.slug).join('-'));
}
